import { ReactNode, useCallback, useRef, useState } from 'react'
import { autoPlacement } from '@floating-ui/core'
import { arrow, shift } from '@floating-ui/dom'
import { autoUpdate, useFloating } from '@floating-ui/react-dom'
import { Popover as HeadlessPopover } from '@headlessui/react'
import classNames from 'classnames'
import { FlexRow } from '@/atoms/FlexContainers'
import { CloseIcon } from '@/atoms/Icons/CloseIcon'
import { MusicIcon } from '@/atoms/Icons/MusicIcon'
import { BlankSwitch } from '@/atoms/Switch/BlankSwitch'
import { CaptionLG } from '@/atoms/Text'
import { Text } from '@/atoms/Text'
import { TheatricalReleaseResult } from '@/services/Theatrical'
import { useTranslate } from '@/utils/translate/translate-client'
import { useTicketsContext } from './TicketsContext'

interface Props {
  theatricalRelease: TheatricalReleaseResult
}

export const SingAlongFilter: React.FC<Props> = ({ theatricalRelease }) => {
  const { releaseTypeFilter, setReleaseTypeFilter } = useTicketsContext()
  const { t } = useTranslate('tickets')
  const triggerRef = useRef<HTMLDivElement>(null)

  const handleToggleReleaseTypeFilter = useCallback(() => {
    setReleaseTypeFilter(
      releaseTypeFilter.includes('SING_ALONG')
        ? releaseTypeFilter.filter((releaseType) => releaseType !== 'SING_ALONG')
        : [...releaseTypeFilter, 'SING_ALONG'],
    )
  }, [releaseTypeFilter, setReleaseTypeFilter])

  return (
    <div className="flex items-center gap-2">
      <Popover
        title={t('singAlongShowtimesTitle', 'Sing-Along Showtimes')}
        description={t(
          'singAlongFilterTooltip',
          'See only showtimes with the Sing-Along version of {{title}}, featuring on-screen lyrics during musical moments.',
          {
            title: theatricalRelease?.title ?? t('thisMovie', 'this movie'),
          },
        )}
        triggerComponent={
          <div className="flex items-center gap-1" ref={triggerRef}>
            <MusicIcon size={16} />
            <CaptionLG>{t('onlySingAlongShowtimes', 'Only Sing-Along showtimes')}</CaptionLG>
          </div>
        }
      />
      <BlankSwitch checked={releaseTypeFilter.includes('SING_ALONG')} onChange={handleToggleReleaseTypeFilter} />
    </div>
  )
}

interface PopoverProps {
  title: string
  description: string | ReactNode
  icon?: ReactNode
  triggerComponent: ReactNode
  buttonClassName?: string
  isDarkMode?: boolean
  className?: string
  defaultOpen?: boolean
}

function Popover({
  title,
  description,
  icon,
  buttonClassName,
  triggerComponent,
  isDarkMode = true,
  className,
  defaultOpen = true,
}: PopoverProps) {
  const [isDefaultOpen, setIsDefaultOpen] = useState(defaultOpen)
  const [arrowEl, setArrowEl] = useState<Element>()
  const { refs, floatingStyles, middlewareData } = useFloating({
    placement: 'bottom-start',
    whileElementsMounted: autoUpdate,
    middleware: [
      autoPlacement({ allowedPlacements: ['bottom', 'bottom-start', 'bottom-end'] }),
      shift(),
      arrow({ element: arrowEl as Element, padding: 18 }),
    ],
  })

  const renderDescription = () => {
    if (typeof description === 'string') {
      const descriptionParagraphs = description.split('\n')
      return descriptionParagraphs.map((paragraph) => (
        <Text
          key={paragraph}
          color={isDarkMode ? 'white' : 'core-gray-950'}
          className="photon-caption-sm md:photon-caption-md mt-2 block font-normal"
        >
          {paragraph}
        </Text>
      ))
    }
    return description
  }

  const handleInteraction = () => {
    setIsDefaultOpen(false)
  }

  return (
    <HeadlessPopover className={classNames('relative z-40', className)}>
      {({ open }) => {
        const isVisible = isDefaultOpen || open

        return (
          <>
            <HeadlessPopover.Button
              ref={refs.setReference}
              className={classNames('cursor-pointer', buttonClassName)}
              onClick={handleInteraction}
            >
              {triggerComponent}
            </HeadlessPopover.Button>
            <HeadlessPopover.Panel
              static
              ref={refs.setFloating}
              style={floatingStyles}
              className={classNames(
                'w-[350px] !top-1.5 p-4 md:p-6 !z-50 text-left rounded-2xl',
                !isVisible && 'hidden',
                isDarkMode
                  ? 'bg-core-gray-800 drop-shadow-light-2 md:drop-shadow-light-3'
                  : 'bg-white drop-shadow-dark-2 md:drop-shadow-dark-3',
              )}
            >
              <div
                ref={(node) => setArrowEl(node as Element)}
                style={{ left: middlewareData.arrow?.x, top: middlewareData.arrow?.y }}
                className={classNames(
                  'absolute -top-1 h-3.5 w-3.5 md:h-4 md:w-4 rounded-[2px] md:rounded-[3px] rotate-45',
                  isDarkMode ? 'bg-core-gray-800' : 'bg-white',
                )}
              />
              <FlexRow className="relative z-50 gap-1">
                <span className="mb-auto"> {icon}</span>
                <Text
                  className="photon-title-xxs md:photon-title-xs font-bold"
                  color={isDarkMode ? 'white' : 'core-gray-950'}
                  weight="bold"
                >
                  {title}
                </Text>
                <HeadlessPopover.Button className="ml-auto" onClick={handleInteraction}>
                  <CloseIcon className="md:hidden" size={18} color={isDarkMode ? 'core-gray-500' : 'core-gray-700'} />
                  <CloseIcon
                    className="hidden md:block"
                    size={20}
                    color={isDarkMode ? 'core-gray-500' : 'core-gray-700'}
                  />
                </HeadlessPopover.Button>
              </FlexRow>

              {renderDescription()}
            </HeadlessPopover.Panel>
          </>
        )
      }}
    </HeadlessPopover>
  )
}
